import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Empty, Row } from 'antd'
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'

import AddQuickEntry from '@/components/AddQuickEntry'
import AddToCartBtn from '@/components/AddToCartBtn'
import B2becPagination from '@/components/B2becPagination/B2becPagination'
import { NotificationManager } from '@/components/Notification'
import ProductComparisonModal from '@/components/ProductComparisonModal'
import B2becCardSkeleton from '@/components/Skeletons/B2becCardSkeleton'
import WishlistProductCard from '@/components/Wishlist/WishlistProductCard'
import {
  useAddProductToWishlist,
  useRemoveProductFromWishlist,
} from '@/pages/WishlistOverview/useFetchWishlist'
import { useAppDispatch } from '@/store/hook'
import { addProductToCart } from '@/store/slices/cartSlice'
import { useDebounce } from '@/hooks'
import usePermission from '@/hooks/usePermission'
import useProductComparison from '@/hooks/useProductComparison'
import { STATUS_CODE } from '@/libs/constants'
import { MODULE_SHOP_NAVIGATION_RIGHTS } from '@/libs/constants/modulerights'
import { isValidArray } from '@/libs/utils/array'

import {
  useFetchWishlistDetails,
  useUpdateProductQuantity,
  useRelocateWishlistItems,
  useIsSharedWishlist,
  WISHLIST_DETAIL_PAGE_SIZE,
} from '../../useWishlistDetails'
import WishlistSearchBar from '../SearchBar'
import SortableProductItem from './SortableProductItem'
import styles from './ProductList.module.scss'

const ProductList = ({
  wishlistId = '',
  wishlistOwnerId = '',
  currentPage = 1,
  onPageChange = () => {},
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const searchQuery = useDebounce(searchTerm, 300)
  const savedPageRef = useRef(null)
  const prevSearchQueryRef = useRef('')

  const currentPageRef = useRef(currentPage)
  currentPageRef.current = currentPage

  const onPageChangeRef = useRef(onPageChange)
  onPageChangeRef.current = onPageChange

  useEffect(() => {
    const wasSearching = prevSearchQueryRef.current !== ''
    const isSearching = searchQuery !== ''

    if (!wasSearching && isSearching) {
      savedPageRef.current = currentPageRef.current
      onPageChangeRef.current(1)
    } else if (wasSearching && !isSearching) {
      if (savedPageRef.current !== null) {
        onPageChangeRef.current(savedPageRef.current)
        savedPageRef.current = null
      }
    }

    prevSearchQueryRef.current = searchQuery
  }, [searchQuery])

  const { data: wishlistData, isLoading: isFetchingWishlist } = useFetchWishlistDetails({
    wishlistOwnerId,
    wishlistId,
    pageNumber: currentPage,
    pageSize: WISHLIST_DETAIL_PAGE_SIZE,
    searchQuery,
  })

  const { mutateAsync: addProductToWishlist } = useAddProductToWishlist()
  const { mutate: removeProductFromWishlist, isPending: isRemovingProduct } =
    useRemoveProductFromWishlist()
  const { mutate: updateProductQuantityMutation } = useUpdateProductQuantity()

  const productList = wishlistData?.items?.items ?? []
  const totalProduct = wishlistData?.totalProduct ?? 0
  const isSharedWishlist = useIsSharedWishlist(
    wishlistOwnerId,
    wishlistId,
    wishlistData?.ownerId,
  )

  const [localProducts, setLocalProducts] = useState(productList)
  const [activeProduct, setActiveProduct] = useState(null)

  useEffect(() => {
    setLocalProducts(wishlistData?.items?.items ?? [])
  }, [wishlistData])

  const { mutate: relocateWishlistItems, isPending: isRelocating } = useRelocateWishlistItems()

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const getItemId = (item) => item?.materialNumber ?? item?.materialId

  const handleDragStart = (event) => {
    const product = localProducts.find((p) => getItemId(p) === event.active.id)
    setActiveProduct(product ?? null)
  }

  const handleDragEnd = (event) => {
    const { active, over } = event
    setActiveProduct(null)

    if (!over || active.id === over.id || isSharedWishlist || Boolean(searchQuery)) return

    const oldIndex = localProducts.findIndex((p) => getItemId(p) === active.id)
    const newIndex = localProducts.findIndex((p) => getItemId(p) === over.id)

    if (oldIndex < 0 || newIndex < 0) return

    const reordered = arrayMove(localProducts, oldIndex, newIndex)
    setLocalProducts(reordered)

    relocateWishlistItems(
      {
        ownerId: wishlistOwnerId,
        wishlistId,
        items: reordered.map((item, index) => ({
          materialNumber: getItemId(item),
          order: (currentPage - 1) * WISHLIST_DETAIL_PAGE_SIZE + index + 1,
        })),
      },
      { onError: () => setLocalProducts(localProducts) },
    )
  }

  const { hasPermission: canViewListPrice } = usePermission(
    MODULE_SHOP_NAVIGATION_RIGHTS.VIEW_LIST_PRICE,
  )

  const {
    onCompareHandler,
    comparingProductIds,
    showComparisonModal,
    setShowComparisonModal,
    productInformation,
  } = useProductComparison()

  const onRemoveProduct = useCallback(
    (materialNumber) => {
      if (wishlistOwnerId && wishlistId && !isSharedWishlist) {
        removeProductFromWishlist({
          wishlistOwnerId,
          wishlistId,
          materialNumber,
        })
      }
    },
    [wishlistOwnerId, wishlistId, isSharedWishlist, removeProductFromWishlist],
  )

  const onChangeProductQuantity = useCallback(
    (productItem) => (newQuantity) => {
      if (
        Number.parseInt(newQuantity, 10) > 0
        && wishlistOwnerId
        && wishlistId
        && !isSharedWishlist
      ) {
        const productData = {
          materialNumber: productItem.materialNumber,
          quantity: Number.parseInt(newQuantity, 10),
        }

        updateProductQuantityMutation({
          wishlistOwnerId,
          wishlistId,
          productData,
        })
      }
    },
    [
      wishlistOwnerId,
      wishlistId,
      isSharedWishlist,
      updateProductQuantityMutation,
    ],
  )

  const onAddItemToWishList = async (materialNumber) => {
    try {
      await addProductToWishlist({
        wishlistId,
        ownerId: wishlistOwnerId,
        materialNumber: materialNumber,
        quantity: 1,
      })
      NotificationManager.success({
        message: 'notification.success.addProductToWishlist',
      })
    } catch (error) {
      let message = 'notification.error.addProductToWishlist'

      try {
        const { data, status } = error ?? {}

        let errorData = data
        if (typeof data === 'string') {
          try {
            errorData = JSON.parse(data)
          } catch {
            // do nothing
          }
        }

        const { errorCode } = errorData ?? {}

        if (
          status === 500
          && Number.parseInt(errorCode, 10) === STATUS_CODE.NOT_FOUND
        ) {
          message = 'errors.productNotFound'
        }
      } catch (parseError) {
        console.error('parseError', parseError)
      }

      NotificationManager.error({
        message,
      })

      throw error
    }
  }

  const onAddProductToCart = useCallback(
    (materialNumber, quantity) => {
      dispatch(addProductToCart({ materialNumber, quantity }))
    },
    [dispatch],
  )

  const onCheckHandler = useCallback(
    (product, event) => {
      const isChecked = event?.target?.checked

      onCompareHandler(isChecked, product)
    },
    [onCompareHandler],
  )

  const onSearchProductHandler = useCallback((event) => {
    setSearchTerm(event.target.value)
  }, [])


  return (
    <>
      <div className={styles['header-container']}>
        <div>
          {!isSharedWishlist && (
            <AddQuickEntry
              onAddItem={onAddItemToWishList}
              currentItems={productList}
              showDeleteButton={false}
              allowAddDuplicate={false}
              duplicateWarningContent={t(
                'wishlist.modal.addProduct.duplicateWarning',
              )}
            />
          )}
        </div>
        <div className={styles['search-product']}>
          <WishlistSearchBar
            searchTerm={searchTerm}
            onSearchHandler={onSearchProductHandler}
          />
        </div>
      </div>
      {isFetchingWishlist
        ? (
          <B2becCardSkeleton
            columns={{ xs: 24, sm: 24, md: 12, lg: 8, xl: 8, xxl: 6 }}
            gutter={20}
            hasPrice={false}
            imageHeight={530}
            number={8}
          />
        )
        : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setActiveProduct(null)}
          >
            {isRelocating
              ? (
                <B2becCardSkeleton
                  columns={{ xs: 24, sm: 24, md: 12, lg: 8, xl: 8, xxl: 6 }}
                  gutter={20}
                  hasPrice={false}
                  imageHeight={530}
                  number={8}
                />
              )
              : (
                <SortableContext
                  items={localProducts.map(getItemId)}
                  strategy={rectSortingStrategy}
                >
                  <Row
                    className="product-card__wrapper"
                    gutter={20}
                    style={{ marginTop: 30 }}
                  >
                    {isValidArray(localProducts)
                      ? (
                        localProducts.map((item) => (
                          <SortableProductItem
                            key={`${getItemId(item)}-${item?.salesOrderNumber}`}
                            id={getItemId(item)}
                            disableDragAndDrop={isSharedWishlist || Boolean(searchQuery)}
                          >
                            {({ dragHandleProps }) => (
                              <WishlistProductCard
                                isCompareCheckboxDisplayed
                                comparingProductIds={comparingProductIds}
                                currencyUnit={item?.currencyUnit ?? item?.currency}
                                displayAddToCart={item?.displayAddToCart}
                                img={item?.img ?? item?.imageUrl ?? item?.pictureUrl}
                                isRemovingProduct={isRemovingProduct}
                                isSharedWishlist={isSharedWishlist}
                                listPrice={item?.listPrice ?? item?.price}
                                materialNumber={item?.materialNumber ?? item?.materialId}
                                name={item?.name}
                                quantity={item?.quantity}
                                requiresInstruction={item?.requiresInstruction}
                                dragHandleProps={(!isSharedWishlist && !searchQuery) ? dragHandleProps : undefined}
                                addToCartBtn={
                                  <AddToCartBtn
                                    handleClick={() => {
                                      onAddProductToCart(
                                        item?.materialNumber ?? item?.materialId,
                                        item?.quantity,
                                      )
                                    }}
                                  />
                                }
                                shouldDisplayListPrice={item?.displayListPrice && canViewListPrice}
                                onCheckHandler={onCheckHandler}
                                onChangeProductQuantityHandler={(newQuantity) =>
                                  onChangeProductQuantity(item)(newQuantity)}
                                onRemoveWishlistProductHandler={() =>
                                  onRemoveProduct(item?.materialNumber ?? item?.materialId)}
                              />
                            )}
                          </SortableProductItem>
                        ))
                      )
                      : (
                        <Empty
                          className={styles['empty-result']}
                          description={t('noData')}
                        />
                      )}
                  </Row>
                </SortableContext>
              )
            }

            <DragOverlay>
              {activeProduct && (
                <WishlistProductCard
                  isDragging
                  isCompareCheckboxDisplayed
                  dragHandleProps={{}}
                  comparingProductIds={[]}
                  currencyUnit={activeProduct?.currencyUnit ?? activeProduct?.currency}
                  displayAddToCart={activeProduct?.displayAddToCart}
                  img={activeProduct?.img ?? activeProduct?.imageUrl ?? activeProduct?.pictureUrl}
                  isRemovingProduct={false}
                  isSharedWishlist={false}
                  listPrice={activeProduct?.listPrice ?? activeProduct?.price}
                  materialNumber={activeProduct?.materialNumber ?? activeProduct?.materialId}
                  name={activeProduct?.name}
                  quantity={activeProduct?.quantity}
                  requiresInstruction={activeProduct?.requiresInstruction}
                  shouldDisplayListPrice={activeProduct?.displayListPrice && canViewListPrice}
                  addToCartBtn={
                    <AddToCartBtn
                      handleClick={() =>
                        onAddProductToCart(
                          activeProduct?.materialNumber ?? activeProduct?.materialId,
                          activeProduct?.quantity,
                        )}
                    />
                  }
                  onCheckHandler={() => {}}
                  onChangeProductQuantityHandler={() => {}}
                  onRemoveWishlistProductHandler={() => {}}
                />
              )}
            </DragOverlay>
          </DndContext>
        )}

      <B2becPagination
        current={currentPage}
        pageSize={WISHLIST_DETAIL_PAGE_SIZE}
        total={totalProduct}
        onPageChange={onPageChange}
      />

      <ProductComparisonModal
        productsData={productInformation}
        setShowModal={setShowComparisonModal}
        showModal={showComparisonModal}
      />
    </>
  )
}

export default ProductList
